import {
  ObjectDocumentAssoc, ObjectDocumentAssocUpload,
  useCreateObjectDocumentAssocMutation,
  useDeleteObjectDocumentAssocMutation,
  useUpdateObjectDocumentAssocMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseObjectDocumentAssocMutationProps = {}

export type UseObjectDocumentAssocMutationResult = {
  createObjectDocumentAssoc: (objectDocumentAssoc: ObjectDocumentAssocUpload, onSuccess: (() => void) | undefined) => void
  updateObjectDocumentAssoc: (objectDocumentAssoc: Partial<ObjectDocumentAssoc>, onSuccess: (() => void) | undefined) => void
  deleteObjectDocumentAssoc: (id: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useObjectDocumentAssocMutation = (props: UseObjectDocumentAssocMutationProps = {}): UseObjectDocumentAssocMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createObjectDocumentAssoc, {isLoading: isCreating}] = useCreateObjectDocumentAssocMutation()
  const [_updateObjectDocumentAssoc, {isLoading: isUpdating}] = useUpdateObjectDocumentAssocMutation()
  const [_deleteObjectDocumentAssoc, {isLoading: isDeleting}] = useDeleteObjectDocumentAssocMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useObjectDocumentAssocUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.objectdocumentassoc", "Document"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useObjectDocumentAssocUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useObjectDocumentAssocUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.objectdocumentassoc", "Document"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useObjectDocumentAssocUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useObjectDocumentAssocUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.objectdocumentassoc", "Document"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useObjectDocumentAssocUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.objectdocumentassoc", "Document") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.objectdocumentassoc", "Document") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.objectdocumentassoc", "Document") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((id: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteObjectDocumentAssoc({id: id}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteObjectDocumentAssoc, handleDeleteResult])

  const createObjectDocumentAssoc = useCallback((objectDocumentAssoc: ObjectDocumentAssocUpload, onSuccess: (() => void) | undefined) => {
    const promise = _createObjectDocumentAssoc(objectDocumentAssoc).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createObjectDocumentAssoc, handleCreateResult])

  const updateObjectDocumentAssoc = useCallback((objectDocumentAssoc: Partial<ObjectDocumentAssoc>, onSuccess: (() => void) | undefined) => {
    const promise = _updateObjectDocumentAssoc(objectDocumentAssoc).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateObjectDocumentAssoc, handleUpdateResult])

  const deleteObjectDocumentAssoc = useCallback((id: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.objectdocumentassoc", "Document"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.objectdocumentassoc.confirm", "Are you sure you want to delete this document?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(id, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createObjectDocumentAssoc: createObjectDocumentAssoc,
    updateObjectDocumentAssoc: updateObjectDocumentAssoc,
    deleteObjectDocumentAssoc: deleteObjectDocumentAssoc,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
